import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Clock } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import RecentlyViewedStrip from "@/components/RecentlyViewedStrip";
import { applySeo, buildBreadcrumbSchema, SITE_INFO } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    applySeo({
      title: "Sacramento Esthetician Blog | Beauty & Balance by Bella",
      description:
        "Honest, practical articles from a downtown Sacramento esthetician — choosing a licensed esty, what to expect from a massage or facial, wedding makeup timelines, and more.",
      path: "/blog",
      type: "website",
      jsonLd: buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
      ]),
    });
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        if (!cancel) setPosts(res?.data?.posts || []);
      } catch (e) {
        // silent
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <main data-testid="blog-index-page" className="relative bg-[#FAF6E8] min-h-screen">
      <Navigation />

      <section className="relative pt-36 md:pt-44 pb-12 md:pb-20 bg-[#0F1F4E] text-[#F8F1D8] overflow-hidden grain">
        <div className="pointer-events-none absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-[#EAD584] blur-3xl opacity-15" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <span className="overline-gold mb-6 inline-block">
            From the studio &middot; {SITE_INFO.city}, {SITE_INFO.region}
          </span>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1] font-light">
            Notes from a <br />
            Sacramento <span className="italic text-[#EAD584]">esthetician.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-[#D6D6BD] font-light">
            Practical, honest writing on skin, bodywork, and beauty in the
            Sacramento area &mdash; from finding the right licensed esty to
            wedding-day timelines.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <RecentlyViewedStrip />
          {posts.length === 0 ? (
            <p className="bb-ink-soft text-center py-20">Loading articles…</p>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              {posts.map((post) => (
                <li
                  key={post.slug}
                  data-testid={`blog-card-${post.slug}`}
                  className="group bg-[#FAF6E8] border border-[#D9CFA0] rounded-lg overflow-hidden transition-all hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(15,31,78,0.25)]"
                >
                  <Link to={`/blog/${post.slug}`} className="block">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={post.hero_image}
                        alt={post.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="p-7 md:p-8">
                      <div className="flex items-center gap-4 mb-4 overline">
                        <span>{new Date(post.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="w-3 h-3" /> {post.read_minutes} min
                        </span>
                      </div>
                      <h2 className="font-serif text-2xl md:text-3xl bb-ink font-light leading-tight mb-3">
                        {post.title}
                      </h2>
                      <p className="bb-ink-soft text-sm leading-relaxed font-light mb-5">
                        {post.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-2 text-sm tracking-wider uppercase bb-ink link-underline text-link-hover">
                        Read article <ArrowUpRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
